import { Component, OnInit, Input, EventEmitter, Output, OnDestroy, OnChanges, SimpleChanges, ElementRef } from '@angular/core';
import { Structure } from '@docma-mm-core/structure/structure';
import { CoEvent, CoEventService } from '@mydocma/co';
import { TreeNode, TreeOptions, TreeService } from '@mydocma/component-library/src/lib/tree';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { PlatformModeStateService } from '../../../mm-offline/services/platform-mode-state.service';
import { ConnectivityService } from '@mydocma/core';
import { AppContainerService } from '@mydocma/app-container';
import { distinctUntilChanged, filter } from 'rxjs/operators';
import { Logger } from '@mydocma/common/src/lib/logger';
import { MmProjectSettingsService } from '../../../mm-project-config/configs/project-settings/mm-project-settings.service';
import { MmStructureSyncService, StructureSyncPatch } from '../mm-structure-sync.service';

interface StructureNode {
  expandable: boolean;
  name: string;
  level: number;
  structure: Structure;
}

@UntilDestroy()
@Component({
  selector: 'mydocma-mm-structure-tree',
  templateUrl: './mm-structure-tree.component.html',
  styleUrls: ['./mm-structure-tree.component.scss']
})
export class MmStructureTreeComponent implements OnInit, OnDestroy, OnChanges {

  private _structures: Structure[] = [];

  @Input()
  set structures(value: Structure[]) {
    this._structures = value || [];
  }

  get structures(): Structure[] {
    return this._structures;
  }

  @Input() selected: Structure;
  @Output() structureClick = new EventEmitter<Structure>();

  treeOptions: TreeOptions;
  treeService: TreeService;
  private resizeObserver: ResizeObserver;
  private maxVisibleLevel: number | null = null;

  constructor(private readonly coEventService: CoEventService,
    private platformModeStateService: PlatformModeStateService,
    private connectivityService: ConnectivityService,
    private readonly appContainerService: AppContainerService,
    private readonly hostRef: ElementRef,
    private readonly projectSettingsService: MmProjectSettingsService,
    private readonly mmStructureSyncService: MmStructureSyncService,
    private readonly logger: Logger) {

    this.mmStructureSyncService.patch$
      .pipe(untilDestroyed(this))
      .subscribe(patch => this.applyStructurePatch(patch));

    this.treeOptions = {
      suppressChildrenCount: true,
      collapseAllTopLevel: 1,
      getNodeId: (data: Structure) => data.Id.toString(),
      // it give s chidren for node , crreate fuctioin
      getNodeChildren: (node: TreeNode) => {
        const children = node.data.Children;
        if (!this.maxVisibleLevel) {
          return children;
        }
        if (node.level >= this.maxVisibleLevel) {
          return null;
        }
        return children;

      },
      getNodeInfo: (node: TreeNode) => {
        return {
          text: [node.data.Name1, node.data.Name2].filter(Boolean).join(" - "),
          expanded: node.data.Level < this.structures[0].Level + 1,
          selectable: node.data.selectable,
          hidden: node.data.hidden
        };
      },
      onReady: (ts, api) => {

        this.treeService = ts;

        this.coEventService.emit(CoEvent.StructureTreeReady, api);
        this.projectSettingsService.maxProjectLevel$
          .pipe(
            untilDestroyed(this),
            distinctUntilChanged()
          )
          .subscribe(level => {
            this.maxVisibleLevel = level;

            if (this.treeService && this._structures?.length) {
              this.treeService.setData([...this.structures] as any);
            }
          });


        this.treeService.selectedNode$
        .pipe(untilDestroyed(this))
        .subscribe(n => {
          if (n) {
            this.treeService.showNode(n);
          }
        });

        this.appContainerService.isLeftPaneOpen
          .pipe(
            untilDestroyed(this),
            distinctUntilChanged(),
            filter(isOpen => isOpen),
          )
          .subscribe(() => {
            setTimeout(() => {
              this.treeService?.gridApi?.sizeColumnsToFit();
            }, 100);
          });

        this.resizeObserver = new ResizeObserver((entries) => {
          const width = entries[0]?.contentRect?.width;
          if (width > 0) {
            this.treeService?.gridApi?.sizeColumnsToFit();
          }
        });
        this.resizeObserver.observe(this.hostRef.nativeElement);

      },
      getNodeClass(node) {
        if (node.data.selectable === false) {
          return "mydocma-mm-readonly-node";
        }
      },
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.structures && this.treeService?.gridApi) {
      this.treeService.gridApi.sizeColumnsToFit();
    }
  }

  ngOnInit() {
  }

  private applyStructurePatch(patch: StructureSyncPatch): void {
    if (!this.treeService) {
      return;
    }
    if (patch.op === 'update') {
      this.treeService.updateNode(patch.node);
    } else if (patch.op === 'delete') {
      this.treeService.removeNode(patch.node);
    } else if (patch.op === 'reorder' && patch.parent) {
      const parentId = patch.parent.Id?.toString();
      const newOrder = patch.parent.Children ?? [];
      if (parentId) {
        this.treeService.reorderChildren(parentId, newOrder);
      }
    } else if (patch.op === 'create' && patch.parent) {
      const parentId = patch.parent.Id?.toString();
      if (parentId) {
        try {
          this.treeService.addNode(patch.node, parentId);
        } catch (e) {
          this.logError('create addNode failed', e, { uid: patch.node?.MyDocmaUid, parentId });
        }
      }
    }
  }

  private logError(stage: string, error: unknown, info: Record<string, unknown>): void {
    const payload = { stage, error: String(error), ...info };
    console.error('Structure sync', stage, error, info);
    this.logger.error(`Structure sync ${stage} ${JSON.stringify(payload)}`, '', 'MmStructureTreeComponent');
  }


  onClick(node: TreeNode) {
    if(this.platformModeStateService.isLocalModeEnabled && this.connectivityService.isOfflineVal && !this.platformModeStateService.isLocalMode) {
      this.platformModeStateService.showNoInternetMessage$.next({show: true, cooldown: false, isLocalMode: false});
      return;
    }
    if (node) {
      this.structureClick.emit(node.data);
    }
  }
}
